import { useState, useEffect, useMemo, useCallback } from "react";
import { useApp } from "../context/AppContext";
import { Icons } from "../components/Icons";
import { Line } from "react-chartjs-2";

const TOP_FILTERS = [
  { key: "all", label: "ВСІ", fn: () => true },
  { key: "top1", label: "ТОП 1", fn: p => p != null && p <= 1 },
  { key: "top3", label: "ТОП 3", fn: p => p != null && p <= 3 },
  { key: "top5", label: "ТОП 5", fn: p => p != null && p <= 5 },
  { key: "top10", label: "ТОП 10", fn: p => p != null && p <= 10 },
  { key: "top30", label: "ТОП 30", fn: p => p != null && p <= 30 },
  { key: "out", label: ">30", fn: p => p == null || p > 30 },
];

// Розкладаємо вибрані ключі по статтях для модуля «Генерація статей для ссилок».
// Кожна стаття = 2 анкори; намагаємось ставити 2 ключі з РІЗНИХ сторінок (без 2 посилань на одну сторінку в одній статті).
function packLinkRows(keys) {
  const buckets = new Map(); // url → [keys]
  keys.forEach(k => { const u = k.url || ""; if (!buckets.has(u)) buckets.set(u, []); buckets.get(u).push(k); });
  const rows = [];
  while (true) {
    const live = [...buckets.values()].filter(a => a.length);
    if (!live.length) break;
    live.sort((a, b) => b.length - a.length); // найбільша корзина першою
    const k1 = live[0].shift();
    let k2 = null;
    for (let i = 1; i < live.length; i++) { if (live[i].length) { k2 = live[i].shift(); break; } } // ключ з ІНШОЇ сторінки
    rows.push({ topic: k1.keyword, anchor1: k1.keyword, url1: k1.url || "", anchor2: k2 ? k2.keyword : "", url2: k2 ? (k2.url || "") : "" });
  }
  return rows;
}

const CHART_TABS = [
  { key: "top1", label: "ТОП 1", stat: "top1" },
  { key: "top3", label: "ТОП 3", stat: "top3" },
  { key: "top5", label: "ТОП 5", stat: "top5" },
  { key: "top10", label: "ТОП 10", stat: "top10" },
  { key: "top20", label: "ТОП 20", stat: "top20" },
  { key: "top30", label: "ТОП 30", stat: "top30" },
];

function getPathname(url) {
  try { return new URL(url).pathname; } catch { return url || "/"; }
}

export default function PositionsView() {
  const { proj, activeId, apiFetch, dark, t, card, inp, btnP, btnG, btnSm, stitle, muted, flash, bgTasks, getStore, setStore, saveClusters, setView, dataRef, userRole } = useApp();
  const isAdmin = userRole === "admin"; // ✎/рамку ручної позиції бачить лише адмін (клієнт=demo — не бачить)

  const [snapshots, setSnapshots] = useState([]);
  const [selSnap, setSelSnap] = useState(0);
  const [structure, setStructure] = useState([]);
  const [checking, setChecking] = useState(false);
  const [progress, setProgress] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [topFilter, setTopFilter] = useState("all");
  const [chartTab, setChartTab] = useState("top10");
  const [expanded, setExpanded] = useState({});
  const [groupSort, setGroupSort] = useState("position");
  const [kwSort, setKwSort] = useState("posAsc");
  const [viewMode, setViewMode] = useState("groups"); // "groups" | "flat"
  const [tags, setTags] = useState({}); // keyword → [tag1, tag2]
  const [tagFilter, setTagFilter] = useState("");
  const [showTagInput, setShowTagInput] = useState(null); // keyword being tagged
  const [tagInput, setTagInput] = useState("");
  const [editCell, setEditCell] = useState(null); // { keyword, snapIdx } — ручне редагування позиції
  const [editVal, setEditVal] = useState("");

  /* ---- Load data ---- */
  const loadData = useCallback(() => {
    if (!activeId) return;
    setLoading(true);
    apiFetch(`/api/projects/${activeId}/positions`).then(r => r.json()).then(d => {
      if (d.snapshots?.length) {
        setSnapshots(d.snapshots);
        setSelSnap(d.snapshots.length - 1);
      } else { setSnapshots([]); setSelSnap(0); }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [activeId, apiFetch]);

  useEffect(() => { loadData(); }, [loadData]);

  // Статті з модуля «Генерація статей для ссилок» (link_articles) — щоб показати скільки статей написано на ключ/групу.
  const [linkArticles, setLinkArticles] = useState([]);
  useEffect(() => {
    if (!activeId) return;
    apiFetch(`/api/projects/${activeId}/links`).then(r => r.json()).then(d => setLinkArticles(Array.isArray(d) ? d : [])).catch(() => setLinkArticles([]));
  }, [activeId, apiFetch]);
  // Скільки статей на ключ (за анкором) + множини id статей на ключ (для дедуплікації по групі).
  const { artByKeyword, idsByKw } = useMemo(() => {
    const cnt = {}, ids = {};
    (linkArticles || []).forEach(a => {
      const keys = new Set([a.anchor1, a.anchor2].map(x => String(x || "").trim().toLowerCase()).filter(Boolean));
      keys.forEach(k => { cnt[k] = (cnt[k] || 0) + 1; (ids[k] = ids[k] || new Set()).add(a.id); });
    });
    return { artByKeyword: cnt, idsByKw: ids };
  }, [linkArticles]);

  // ── Вибір ключів галочками → відправка у «Генерацію статей для ссилок» ──
  const [selectedKw, setSelectedKw] = useState(() => new Set());
  const toggleKwSelect = useCallback((k) => {
    const key = String(k.keyword || "").toLowerCase();
    setSelectedKw(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }, []);
  const clearSelect = () => setSelectedKw(new Set());

  // Load/save tags — merge from clusters + positionTags
  useEffect(() => {
    if (!activeId) return;
    const merged = {};
    // 1. Tags from clusters (semantics)
    const store = getStore(activeId);
    (store.clusters || []).forEach(c => {
      (c.keywords || []).forEach(k => {
        if (typeof k !== "string" && k.tags) {
          const kl = (k.keyword || "").toLowerCase();
          const clusterTags = k.tags.split(",").map(t => t.trim()).filter(Boolean);
          if (clusterTags.length) merged[kl] = [...(merged[kl] || []), ...clusterTags];
        }
      });
    });
    // 2. Merge with positionTags from onboarding
    apiFetch(`/api/projects/${activeId}/onboarding`).then(r => r.json()).then(d => {
      if (d?.positionTags) {
        Object.entries(d.positionTags).forEach(([kl, ptags]) => {
          merged[kl] = [...new Set([...(merged[kl] || []), ...ptags])];
        });
      }
      // Deduplicate
      Object.keys(merged).forEach(k => { merged[k] = [...new Set(merged[k])]; });
      setTags(merged);
    }).catch(() => { setTags(merged); });
  }, [activeId]);

  const saveTags = (newTags) => {
    setTags(newTags);
    apiFetch(`/api/projects/${activeId}/onboarding`, {
      method: "PUT", body: JSON.stringify({ positionTags: newTags }),
    }).catch(() => {});
  };

  // Sync tag to clusters (semantics)
  const syncTagToClusters = (keyword, tag, action) => {
    const kl = keyword.toLowerCase();
    const s = getStore(activeId);
    let changed = false;
    const updated = (s.clusters || []).map(c => {
      const newKws = (c.keywords || []).map(k => {
        const kw = typeof k === "string" ? k : k.keyword;
        if (kw.toLowerCase() !== kl) return k;
        changed = true;
        const existing = typeof k === "string" ? { keyword: k, frequency: 0 } : { ...k };
        let kwTags = (existing.tags || "").split(",").map(t => t.trim()).filter(Boolean);
        if (action === "add" && !kwTags.includes(tag)) kwTags.push(tag);
        if (action === "remove") kwTags = kwTags.filter(t => t !== tag);
        return { ...existing, tags: kwTags.join(",") };
      });
      return { ...c, keywords: newKws };
    });
    if (changed) {
      setStore(activeId, { ...s, clusters: updated });
      saveClusters(activeId, updated);
    }
  };

  const addTag = (keyword, tag) => {
    if (!tag.trim()) return;
    const kl = keyword.toLowerCase();
    const cur = tags[kl] || [];
    if (cur.includes(tag.trim())) return;
    saveTags({ ...tags, [kl]: [...cur, tag.trim()] });
    syncTagToClusters(keyword, tag.trim(), "add");
    setShowTagInput(null); setTagInput("");
  };

  const removeTag = (keyword, tag) => {
    const kl = keyword.toLowerCase();
    const cur = (tags[kl] || []).filter(t => t !== tag);
    const next = { ...tags };
    if (cur.length) next[kl] = cur; else delete next[kl];
    saveTags(next);
    syncTagToClusters(keyword, tag, "remove");
  };

  const allTags = useMemo(() => [...new Set(Object.values(tags).flat())].sort(), [tags]);

  useEffect(() => {
    if (!activeId) return;
    apiFetch(`/api/projects/${activeId}/structure`).then(r => r.json()).then(d => {
      if (!d) return;
      // Flatten tree to array of {url, name}
      const flatten = (nodes) => {
        const out = [];
        (nodes || []).forEach(n => {
          if (n.url) out.push({ url: n.url, name: n.name });
          if (n.children) out.push(...flatten(n.children));
        });
        return out;
      };
      const pages = Array.isArray(d) ? flatten(d) : d.children ? flatten(d.children) : d.pages || [];
      setStructure(pages);
    }).catch(() => {});
  }, [activeId, apiFetch]);

  /* ---- Background task watcher ---- */
  useEffect(() => {
    const task = (bgTasks || []).find(bt => bt.projectId === activeId && bt.type === "positions_check");
    if (task?.status === "running") {
      setChecking(true);
      if (task.id) setCheckTaskId(task.id);
      const p = task.progress || {};
      setProgress(p.message || "Перевіряю...");
      setProgressData({ current: p.current || 0, total: p.total || 0 });
    } else if (task?.status === "done" && checking) {
      setChecking(false); setProgress(null); setProgressData(null); setStopping(false); setCheckTaskId(null);
      loadData();
    } else if (task?.status === "error" && checking) {
      setChecking(false); setProgress(null); setProgressData(null); setStopping(false); setCheckTaskId(null);
    }
  }, [bgTasks]);

  const [geoModal, setGeoModal] = useState(false);
  const [geo, setGeo] = useState({ gl: "ua", country: "Ukraine", city: "", location: "Ukraine" });
  const [kwModal, setKwModal] = useState(null); // selected keyword for popup
  const [stopping, setStopping] = useState(false);
  const [checkTaskId, setCheckTaskId] = useState(null); // taskId of running position check (for Stop)

  // Load current geo on mount
  useEffect(() => {
    if (!activeId) return;
    apiFetch(`/api/projects/${activeId}/positions/geo`)
      .then(r => r.json())
      .then(d => { if (d && d.gl) setGeo(d); })
      .catch(() => {});
  }, [activeId, apiFetch]);

  const runCheckWithGeo = async (g) => {
    setChecking(true); setProgress("Запуск..."); setProgressData({ current: 0, total: 0 });
    try {
      const resp = await apiFetch(`/api/projects/${activeId}/positions/check`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ geo: g }),
      });
      const d = await resp.json();
      if (d.ok) { if (d.taskId) setCheckTaskId(d.taskId); flash("Перевірка позицій запущена у фоні"); }
      else { flash("Помилка: " + (d.error || "невідома")); setChecking(false); setProgress(null); setProgressData(null); }
    } catch (e) { flash("Помилка: " + e.message); setChecking(false); setProgress(null); setProgressData(null); }
  };

  // Зберегти регіон БЕЗ запуску перевірки — історія позицій зберігається.
  const saveGeoOnly = async (g) => {
    try {
      const resp = await apiFetch(`/api/projects/${activeId}/positions/geo`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ geo: g }),
      });
      const d = await resp.json();
      if (d.ok) { setGeo(d.geo || g); setGeoModal(false); flash("Регіон збережено. Застосується з наступної перевірки — історія позицій збережена."); }
      else flash("Помилка: " + (d.error || "невідома"));
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const stopCheck = async () => {
    const task = (bgTasks || []).find(bt => bt.projectId === activeId && bt.type === "positions_check" && bt.status === "running");
    const taskId = task?.id || checkTaskId;
    if (!taskId) { setChecking(false); setProgress(null); setProgressData(null); return; }
    setStopping(true);
    try {
      await apiFetch(`/api/projects/${activeId}/positions/check/stop`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ taskId }),
      });
      flash("Зняття позицій зупиняється...");
    } catch (e) { flash("Помилка: " + e.message); setStopping(false); }
  };

  const runCheck = async () => {
    // First time (no snapshots) — show geo picker.
    if (!snapshots || snapshots.length === 0) {
      setGeoModal(true);
      return;
    }
    await runCheckWithGeo(geo);
  };

  /* ---- Derived data ---- */
  const exportHistory = async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/positions/history.csv`);
      if (!r.ok) { flash("Помилка експорту історії"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `positions-history-${activeId}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { flash("Помилка експорту історії"); }
  };

  const current = snapshots[selSnap] || null;
  const prevSnap = selSnap > 0 ? snapshots[selSnap - 1] : null;

  const prevMap = useMemo(() => {
    if (!prevSnap?.keywords) return {};
    const m = {};
    prevSnap.keywords.forEach(k => { m[k.keyword.toLowerCase()] = k.position; });
    return m;
  }, [prevSnap]);

  const structureMap = useMemo(() => {
    const m = {};
    (structure || []).forEach(p => {
      if (!p.url) return;
      const name = p.name || p.title || getPathname(p.url);
      m[p.url] = name;
      m[p.url.replace(/\/+$/, "")] = name;
      m[p.url.replace(/\/+$/, "") + "/"] = name;
    });
    return m;
  }, [structure]);

  // Apply tag filter to keywords for a snapshot
  const applyTagFilter = (kws) => {
    if (!tagFilter || !kws) return kws || [];
    return kws.filter(k => (tags[k.keyword.toLowerCase()] || []).includes(tagFilter));
  };

  /* ---- Top filter stats with changes (respects tag filter) ---- */
  const filterStats = useMemo(() => {
    const kws = applyTagFilter(current?.keywords);
    const prevKws = applyTagFilter(prevSnap?.keywords);
    const total = kws.length;
    return TOP_FILTERS.map(f => {
      const count = kws.filter(k => f.fn(k.position)).length;
      const prevCount = prevKws.length ? prevKws.filter(k => f.fn(k.position)).length : 0;
      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
      const change = prevKws.length ? count - prevCount : null;
      return { ...f, count, pct, change };
    });
  }, [current, prevSnap, tagFilter, tags]);

  /* ---- Filter keywords by top filter + search ---- */
  const filtered = useMemo(() => {
    if (!current?.keywords) return [];
    let data = current.keywords;
    if (topFilter !== "all") {
      const filterDef = TOP_FILTERS.find(f => f.key === topFilter);
      if (filterDef) data = data.filter(k => filterDef.fn(k.position));
    }
    if (search) {
      const sq = search.toLowerCase();
      data = data.filter(k => k.keyword.toLowerCase().includes(sq) || (k.url || "").toLowerCase().includes(sq));
    }
    if (tagFilter) {
      data = data.filter(k => (tags[k.keyword.toLowerCase()] || []).includes(tagFilter));
    }
    return data;
  }, [current, topFilter, search, tagFilter, tags]);

  /* ---- Group by URL ---- */
  const groups = useMemo(() => {
    const map = {};
    filtered.forEach(k => {
      const url = k.url || k.serpUrl || "unknown";
      if (!map[url]) map[url] = { url, keywords: [], totalFreq: 0, posSum: 0, posCount: 0, foundCount: 0 };
      map[url].keywords.push(k);
      map[url].totalFreq += (k.frequency || 0);
      // Середня по ВСІХ ключах сторінки: ключ поза ТОП (position == null) рахується як 100,
      // інакше середня бреше (показує 1, хоча більшість ключів не ранжується).
      const found = k.position != null;
      const pos = found ? k.position : 100;
      map[url].posSum += pos; map[url].posCount++;
      if (found) map[url].foundCount++;
    });
    let arr = Object.values(map);
    arr.forEach(g => { g.avgPos = g.posCount > 0 ? Math.round((g.posSum / g.posCount) * 10) / 10 : 999; });
    // Скільки УНІКАЛЬНИХ статей написано на групу (об'єднання статей по всіх ключах сторінки).
    arr.forEach(g => {
      const s = new Set();
      g.keywords.forEach(k => { const set = idsByKw[String(k.keyword || "").toLowerCase()]; if (set) set.forEach(id => s.add(id)); });
      g.articleCount = s.size;
    });
    arr.sort((a, b) => {
      if (groupSort === "position") return a.avgPos - b.avgPos;
      if (groupSort === "avgPos") return a.avgPos - b.avgPos;
      return b.keywords.length - a.keywords.length;
    });
    // Sort keywords within each group
    arr.forEach(g => {
      g.keywords.sort((a, b) => {
        if (kwSort === "posAsc") return (a.position || 999) - (b.position || 999);
        if (kwSort === "posDesc") return (b.position || 0) - (a.position || 0);
        return (b.frequency || 0) - (a.frequency || 0);
      });
    });
    return arr;
  }, [filtered, groupSort, kwSort, idsByKw]);

  const selectAllFiltered = () => {
    const all = new Set(filtered.map(k => String(k.keyword).toLowerCase()));
    setSelectedKw(prev => prev.size >= all.size ? new Set() : all); // тоггл: всі / зняти
  };
  const sendToLinkGen = () => {
    if (!selectedKw.size) return;
    const byKey = {};
    (current?.keywords || []).forEach(k => { const kl = String(k.keyword).toLowerCase(); if (!byKey[kl]) byKey[kl] = { keyword: k.keyword, url: k.url || "" }; });
    const keys = [...selectedKw].map(kl => byKey[kl]).filter(Boolean);
    if (!keys.length) { flash && flash("Ключі не знайдено у поточному знімку"); return; }
    const rows = packLinkRows(keys);
    const sk = activeId || "_linkgen";
    if (dataRef && dataRef.current) dataRef.current[sk + "_linkgen_pending"] = rows;
    clearSelect();
    flash && flash(`${keys.length} ключів → у генерацію рейтингових (${rows.length} статей)`);
    if (setView) setView("linkgen");
  };

  // ── SparkTraffic: відкрити модалку «Нагнати трафік» для вибраних ключів ──
  const [stModal, setStModal] = useState(null); // { keys: [{keyword, url}] }
  const openSparkTraffic = () => {
    if (!selectedKw.size) return;
    const byKey = {};
    (current?.keywords || []).forEach(k => { const kl = String(k.keyword).toLowerCase(); if (!byKey[kl]) byKey[kl] = { keyword: k.keyword, url: k.url || "" }; });
    const keys = [...selectedKw].map(kl => byKey[kl]).filter(Boolean).filter(k => k.url);
    if (!keys.length) { flash && flash("У вибраних ключів немає URL сторінки у поточному знімку"); return; }
    setStModal({ keys });
  };

  // Масово навісити тег на ключі (авто-тег «ПФ» після підключення трафіку). Тег одразу доступний у фільтрі.
  const applyTagBulk = (keywords, tag) => {
    if (!tag || !keywords || !keywords.length) return;
    setTags(prev => {
      const next = { ...prev };
      keywords.forEach(kw => {
        const kl = String(kw).toLowerCase();
        const cur = next[kl] || [];
        if (!cur.includes(tag)) next[kl] = [...cur, tag];
      });
      apiFetch(`/api/projects/${activeId}/onboarding`, { method: "PUT", body: JSON.stringify({ positionTags: next }) }).catch(() => {});
      return next;
    });
    keywords.forEach(kw => syncTagToClusters(kw, tag, "add"));
    flash && flash(`Тег «${tag}» додано до ${keywords.length} ключів`);
  };

  /* ---- Топ-5 конкурентів (з найранішого знімку, де є дані конкурентів) ---- */
  const topCompetitors = useMemo(() => {
    if (!snapshots?.length) return null;
    // Конкуренти збираються лише з певного знімку — беремо найраніший, де вони є.
    let snap = null;
    for (const s of snapshots) {
      const has = (s.keywords || []).some(k => Array.isArray(k.competitors) && k.competitors.length);
      if (has) { snap = s; break; }
    }
    if (!snap) return null;
    const byDomain = {};
    let ourDomain = (proj?.domain || "").toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];
    (snap.keywords || []).forEach(k => {
      (k.competitors || []).forEach(c => {
        const dom = (c.domain || "").toLowerCase().replace(/^www\./, "");
        if (!dom || dom === ourDomain) return;
        if (!byDomain[dom]) byDomain[dom] = { domain: dom, count: 0, posSum: 0 };
        byDomain[dom].count++;
        byDomain[dom].posSum += (c.position || 0);
      });
    });
    const list = Object.values(byDomain)
      .map(d => ({ ...d, avgPos: d.count ? Math.round((d.posSum / d.count) * 10) / 10 : 0 }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
    return list.length ? { date: snap.date, list } : null;
  }, [snapshots, proj]);

  /* ---- Конкуренти, вказані клієнтом (proj.competitors) — їх присутність у SERP по ключах ---- */
  const clientCompetitors = useMemo(() => {
    const norm = x => (x || "").toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];
    const wanted = (proj?.competitors || []).map(norm).filter(Boolean);
    if (!wanted.length) return null;
    // Той самий знімок, що й для «Топ-5 у видачі» (найраніший зі зібраними конкурентами).
    let snap = null;
    for (const s of (snapshots || [])) {
      if ((s.keywords || []).some(k => Array.isArray(k.competitors) && k.competitors.length)) { snap = s; break; }
    }
    const agg = {};
    wanted.forEach(d => { agg[d] = { domain: d, count: 0, posSum: 0, best: null }; });
    if (snap) (snap.keywords || []).forEach(k => {
      (k.competitors || []).forEach(c => {
        const dom = norm(c.domain);
        if (!agg[dom]) return;
        agg[dom].count++;
        agg[dom].posSum += (c.position || 0);
        if (c.position && (agg[dom].best == null || c.position < agg[dom].best)) agg[dom].best = c.position;
      });
    });
    const list = wanted.map(d => {
      const e = agg[d];
      return { domain: d, count: e.count, avgPos: e.count ? Math.round((e.posSum / e.count) * 10) / 10 : null, best: e.best };
    });
    return { date: snap?.date, hasData: !!snap, list };
  }, [snapshots, proj]);

  /* ---- Chart data ---- */
  const chartData = useMemo(() => {
    if (!snapshots.length) return null;
    const tab = CHART_TABS.find(ct => ct.key === chartTab);
    if (!tab) return null;
    const labels = snapshots.map(sn => sn.date?.slice(5) || "");
    const values = snapshots.map(sn => {
      const kws = applyTagFilter(sn.keywords);
      const total = kws.length;
      if (total === 0) return 0;
      let count;
      if (tab.key === "top1") count = kws.filter(k => k.position != null && k.position <= 1).length;
      else if (tab.key === "top3") count = kws.filter(k => k.position != null && k.position <= 3).length;
      else if (tab.key === "top5") count = kws.filter(k => k.position != null && k.position <= 5).length;
      else if (tab.key === "top10") count = kws.filter(k => k.position != null && k.position <= 10).length;
      else if (tab.key === "top20") count = kws.filter(k => k.position != null && k.position <= 20).length;
      else if (tab.key === "top30") count = kws.filter(k => k.position != null && k.position <= 30).length;
      else count = kws.filter(k => k.position != null).length;
      return Math.round((count / total) * 100);
    });
    return {
      labels,
      datasets: [{
        label: tab.label + " %",
        data: values,
        borderColor: "#6366f1",
        backgroundColor: "rgba(99,102,241,0.08)",
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: "#6366f1",
        borderWidth: 2,
      }],
    };
  }, [snapshots, chartTab, tagFilter, tags]);

  const chartOpts = useMemo(() => {
    // Адаптивна вісь Y: підлаштовуємо максимум під дані (а не завжди 100%),
    // щоб графік не був завжди притиснутий донизу. Округлюємо вгору до 10, +15% запасу, мін 10, кап 100.
    const _vals = (chartData && chartData.datasets && chartData.datasets[0] && chartData.datasets[0].data) || [];
    const _dmax = _vals.length ? Math.max.apply(null, _vals) : 0;
    const _ymax = Math.min(100, Math.max(10, Math.ceil((_dmax * 1.15) / 10) * 10));
    return ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ctx.parsed.y + "%" } } },
    scales: {
      x: { grid: { color: dark ? "#252535" : "#f0f0f0" }, ticks: { color: t.textMuted, font: { size: 10 } } },
      y: { min: 0, max: _ymax, grid: { color: dark ? "#252535" : "#f0f0f0" }, ticks: { color: t.textMuted, font: { size: 10 }, callback: v => v + "%" } },
    },
    });
  }, [dark, t, chartData]);

  /* ---- Helpers ---- */
  const posBadge = (pos) => {
    if (pos == null) return <span style={{ fontSize: 11, color: t.textMuted }}>--</span>;
    const color = pos <= 1 ? "#10b981" : pos <= 3 ? "#10b981" : pos <= 5 ? "#3b82f6" : pos <= 10 ? "#6366f1" : pos <= 30 ? "#f59e0b" : "#ef4444";
    return <span style={{ fontSize: 12, fontWeight: 700, color, padding: "2px 8px", borderRadius: 6, background: `${color}15` }}>{pos}</span>;
  };

  const changeArrow = (keyword, curPos) => {
    if (curPos == null) return null;
    const prev = prevMap[keyword.toLowerCase()];
    if (prev == null) return <span style={{ fontSize: 9, color: "#3b82f6", fontWeight: 700 }}>NEW</span>;
    const diff = prev - curPos;
    if (diff === 0) return <span style={{ fontSize: 10, color: t.textMuted }}>--</span>;
    return (
      <span style={{ fontSize: 11, fontWeight: 700, color: diff > 0 ? "#10b981" : "#ef4444" }}>
        {diff > 0 ? "\u2191" : "\u2193"}{Math.abs(diff)}
      </span>
    );
  };

  // Зберегти ручну позицію ключа в знімку → бек перераховує stats, локально патчимо (графік/таблиця/статистика підтягнуть).
  const saveManualPosition = async (snapIdx, keyword, url, rawVal) => {
    const snap = snapshots[snapIdx];
    setEditCell(null);
    if (!snap) return;
    const val = (rawVal !== undefined ? rawVal : editVal).trim();
    const payload = { date: snap.date, keyword, url: url || "", position: val === "" ? null : parseInt(val, 10) };
    if (val !== "" && (isNaN(payload.position) || payload.position < 1)) { flash("Позиція — число ≥ 1"); return; }
    try {
      const r = await apiFetch(`/api/projects/${activeId}/positions/keyword`, { method: "PUT", body: JSON.stringify(payload) });
      const d = await r.json();
      if (r.ok) {
        setSnapshots(prev => prev.map((s, i) => i === snapIdx ? { ...s, keywords: d.keywords, stats: d.stats } : s));
        flash(val === "" ? "Скинуто на авто" : `Позицію встановлено: ${payload.position}`);
      } else flash("Помилка: " + (d.error || "невідома"));
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const posCell = (keyword, snapIdx) => {
    const snap = snapshots[snapIdx];
    if (!snap?.keywords) return <td style={cellStyle}>--</td>;
    const kw = snap.keywords.find(k => k.keyword.toLowerCase() === keyword.toLowerCase());
    const pos = kw?.position;
    const openEdit = (e) => {
      e.stopPropagation(); // не відкривати модалку ключа
      const rc = e.currentTarget.getBoundingClientRect();
      setEditVal(pos == null ? "" : String(pos));
      setEditCell({ keyword, snapIdx, url: kw?.url || "", pos: pos ?? null, manual: !!kw?.manual, rect: { left: rc.left, top: rc.top, bottom: rc.bottom } });
    };
    if (pos == null) return <td style={{ ...cellStyle, cursor: isAdmin ? "pointer" : "default" }} onClick={isAdmin ? openEdit : undefined} title={isAdmin ? "Не знайдено в ТОП. Клік — внести вручну" : undefined}><span style={{ color: "#ef4444", fontSize: 11, fontWeight: 600 }}>&gt;100</span></td>;
    const prevSnapIdx = snapIdx - 1;
    let diff = null;
    if (prevSnapIdx >= 0) {
      const prevKw = snapshots[prevSnapIdx]?.keywords?.find(k => k.keyword.toLowerCase() === keyword.toLowerCase());
      if (prevKw?.position != null) diff = prevKw.position - pos;
    }
    const color = pos <= 3 ? "#10b981" : pos <= 10 ? "#6366f1" : pos <= 30 ? "#f59e0b" : "#ef4444";
    return (
      <td style={{ ...cellStyle, cursor: isAdmin ? "pointer" : "default" }} onClick={isAdmin ? openEdit : undefined} title={!isAdmin ? undefined : (kw?.manual ? "Ручна позиція. Клік — змінити або скинути" : "Клік — внести вручну")}>
        <span style={{ fontSize: 12, fontWeight: 700, color, padding: "2px 6px", borderRadius: 5, background: `${color}12`, border: (kw?.manual && isAdmin) ? `1px solid ${color}` : "1px solid transparent" }}>{pos}{(kw?.manual && isAdmin) ? <span style={{ fontSize: 8, marginLeft: 2 }} title="вручну">✎</span> : null}</span>
        {diff != null && diff !== 0 && (
          <span style={{ fontSize: 9, fontWeight: 700, color: diff > 0 ? "#10b981" : "#ef4444", marginLeft: 3 }}>
            {diff > 0 ? "\u2191" : "\u2193"}{Math.abs(diff)}
          </span>
        )}
      </td>
    );
  };

  const toggleGroup = (url) => setExpanded(prev => ({ ...prev, [url]: !prev[url] }));

  const bd = `1px solid ${dark ? "#1e1e2c" : "#eee"}`;
  const cellStyle = { padding: "5px 10px", borderBottom: bd, textAlign: "center", fontSize: 12 };
  const cellLeft = { ...cellStyle, textAlign: "left" };

  /* ---- Snapshot columns for table (last 5 max) ---- */
  const snapCols = useMemo(() => {
    const start = Math.max(0, snapshots.length - 5);
    return snapshots.slice(start).map((sn, i) => ({ idx: start + i, label: formatDateUkr(sn.date) || `#${start + i + 1}` }));
  }, [snapshots]);

  const onSnapColClick = (idx) => {
    if (selSnap === idx) {
      setKwSort(s => s === "posAsc" ? "posDesc" : "posAsc");
    } else {
      setSelSnap(idx);
      setKwSort("posAsc");
    }
  };

  if (!proj) return null;

  /* ---- RENDER ---- */
  return (
    <div>
      {geoModal && (
        <GeoModal initial={geo} onClose={() => setGeoModal(false)}
          hasHistory={(snapshots || []).length > 0}
          onSaveOnly={saveGeoOnly}
          onSubmit={async (g) => { setGeo(g); setGeoModal(false); await runCheckWithGeo(g); }} t={t} dark={dark} btnP={btnP} btnG={btnG} inp={inp} />
      )}
      {editCell && (() => {
        const r = editCell.rect || { left: 200, top: 200, bottom: 210 };
        const W = 234;
        const left = Math.max(8, Math.min(r.left, window.innerWidth - W - 12));
        let top = r.bottom + 6;
        if (top > window.innerHeight - 180) top = Math.max(8, r.top - 180);
        return (
          <>
            <div onClick={() => setEditCell(null)} style={{ position: "fixed", inset: 0, zIndex: 998 }} />
            <div style={{ position: "fixed", left, top, width: W, zIndex: 999, background: dark ? "#1a1a2e" : "#fff", color: t.text, border: `1px solid ${t.border}`, borderRadius: 10, boxShadow: "0 10px 34px rgba(0,0,0,.28)", padding: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 3 }}>Внести вручну</div>
              <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={editCell.keyword}>{editCell.keyword}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 11 }}>
                <span style={{ fontSize: 12, color: t.textMuted }}>Позиція</span>
                <input autoFocus type="number" min="1" value={editVal}
                  onChange={e => setEditVal(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") saveManualPosition(editCell.snapIdx, editCell.keyword, editCell.url); if (e.key === "Escape") setEditCell(null); }}
                  placeholder="—" style={{ ...inp, width: 72, textAlign: "center", fontSize: 15, fontWeight: 700 }} />
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => saveManualPosition(editCell.snapIdx, editCell.keyword, editCell.url)} style={{ ...btnP, flex: 1, padding: "6px 8px", fontSize: 12 }}>Зберегти</button>
                {editCell.manual && <button onClick={() => saveManualPosition(editCell.snapIdx, editCell.keyword, editCell.url, "")} title="Повернути автоматичну позицію" style={{ ...btnG, padding: "6px 8px", fontSize: 12 }}>Скинути</button>}
              </div>
              <div style={{ fontSize: 10, color: t.textMuted, marginTop: 8 }}>Enter — зберегти · Esc — закрити{editCell.manual ? " · зараз вручну ✎" : ""}</div>
            </div>
          </>
        );
      })()}
      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Перевірка позицій</h2>
        {snapshots.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <select value={selSnap} onChange={e => setSelSnap(Number(e.target.value))}
              style={{ ...inp, fontSize: 12, padding: "4px 8px", width: "auto" }}>
              {snapshots.map((sn, i) => (
                <option key={i} value={i}>{formatDateUkr(sn.date) || sn.date}</option>
              ))}
            </select>
            <button onClick={async () => {
              const snap = snapshots[selSnap];
              if (!snap?.date || !confirm(`Видалити знімок за ${snap.date}?`)) return;
              try {
                await apiFetch(`/api/projects/${activeId}/positions/${snap.date}`, { method: "DELETE" });
                flash(`Знімок ${snap.date} видалено`);
                const next = snapshots.filter((_, i) => i !== selSnap);
                setSnapshots(next);
                setSelSnap(Math.max(0, selSnap - 1));
              } catch (e) { flash("Помилка: " + e.message); }
            }} style={{ ...btnSm, color: "#ef4444", borderColor: "#ef444433", fontSize: 11, padding: "4px 8px" }} title="Видалити знімок">{Icons.trash(12)}</button>
          </div>
        )}
        {/* View mode toggle */}
        <div style={{ marginLeft: "auto", display: "flex", gap: 2, background: dark ? "#252535" : "#f3f4f6", borderRadius: 6, padding: 2 }}>
          {[{ key: "groups", label: "Групи" }, { key: "flat", label: "Всі ключі" }].map(m => (
            <button key={m.key} onClick={() => setViewMode(m.key)} style={{
              padding: "4px 10px", borderRadius: 4, border: "none", fontSize: 11, fontWeight: 600, cursor: "pointer",
              background: viewMode === m.key ? "#6366f1" : "transparent",
              color: viewMode === m.key ? "#fff" : t.textMuted, fontFamily: "inherit",
            }}>{m.label}</button>
          ))}
        </div>
        {/* Tag filter */}
        {allTags.length > 0 && (
          <select value={tagFilter} onChange={e => setTagFilter(e.target.value)}
            style={{ ...inp, fontSize: 12, padding: "5px 8px", width: "auto", fontWeight: tagFilter ? 700 : 400, borderColor: tagFilter ? "#6366f1" : t.border, color: tagFilter ? "#6366f1" : t.text }}>
            <option value="">Всі теги</option>
            {allTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
          </select>
        )}
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Пошук..."
          style={{ ...inp, fontSize: 12, padding: "6px 10px", width: 180 }} />
        <button onClick={() => setGeoModal(true)} title="Регіон зняття позицій · натисніть щоб змінити (історія збережеться)"
          style={{ ...btnG, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <span>🌍</span>{geo.city ? `${geo.city}, ` : ""}{geo.country || "Ukraine"}
          <span style={{ opacity: 0.55, fontFamily: "monospace", fontSize: 11 }}>gl={geo.gl}</span>
        </button>
        <button onClick={runCheck} disabled={checking}
          style={{ ...btnP, fontSize: 12, opacity: checking ? 0.6 : 1 }}>
          {checking ? "Перевіряю..." : "Перевірити"}
        </button>
        {checking && (
          <button onClick={stopCheck} disabled={stopping}
            style={{ ...btnG, fontSize: 12, color: "#ef4444", borderColor: "#ef444455", opacity: stopping ? 0.6 : 1 }}
            title="Зупинити зняття позицій">
            {stopping ? "Зупиняю..." : "⏹ Зупинити"}
          </button>
        )}
        <button onClick={exportHistory} title="Вивантажити історію позицій (усі зняття) у CSV: рядок=ключ, колонки=дати"
          style={{ ...btnG, fontSize: 12, whiteSpace: "nowrap" }}>⬇ Історія CSV</button>
      </div>

      {/* Tag filter banner */}
      {tagFilter && (
        <div style={{ padding: "8px 14px", marginBottom: 8, borderRadius: 10, background: dark ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.04)", border: `1px solid ${dark ? "rgba(99,102,241,0.2)" : "rgba(99,102,241,0.1)"}`, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#6366f1", display: "inline-flex", alignItems: "center", gap: 4 }}>{Icons.tag(12, "#6366f1")} Фільтр: {tagFilter}</span>
          <span style={{ fontSize: 11, color: t.textMuted }}>Графік і статистика перераховані тільки для ключів з цим тегом</span>
          <button onClick={() => setTagFilter("")} style={{ ...btnSm, marginLeft: "auto", fontSize: 11 }}>✕ Скинути</button>
        </div>
      )}

      {/* Progress bar */}
      {checking && progressData && (
        <div style={{ ...card, padding: "10px 14px", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>{progress}</span>
            <span style={{ fontSize: 11, color: t.textMuted, marginLeft: "auto" }}>{progressData.current}/{progressData.total}</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: dark ? "#252535" : "#e5e7eb", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progressData.total > 0 ? (progressData.current / progressData.total) * 100 : 0}%`,
              background: "linear-gradient(90deg, #6366f1, #8b5cf6)", borderRadius: 3, transition: "width .3s" }} />
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ ...card, textAlign: "center", padding: 40, color: t.textMuted }}>Завантаження...</div>
      ) : !current ? (
        <div style={{ ...card, textAlign: "center", padding: 48 }}>
          <div style={{ marginBottom: 10, opacity: 0.5 }}>{Icons.chart(40)}</div>
          <p style={{ fontWeight: 600, marginBottom: 6 }}>Немає знімків позицій</p>
          <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 16 }}>Перевірте позиції для всіх ключів вашої семантики</p>
          <button onClick={runCheck} disabled={checking} style={btnP}>{checking ? progress : "Перевірити"}</button>
          {checking && <button onClick={stopCheck} disabled={stopping} style={{ ...btnG, marginLeft: 8, color: "#ef4444", borderColor: "#ef444455" }} title="Зупинити зняття позицій">{stopping ? "Зупиняю..." : "⏹ Зупинити"}</button>}
        </div>
      ) : (
        <>
          {/* TOP FILTER BAR */}
          <div style={{ ...card, padding: "8px 12px", marginBottom: 14, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {filterStats.map(f => {
              const active = topFilter === f.key;
              return (
                <button key={f.key} onClick={() => { setTopFilter(f.key); }}
                  style={{
                    padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600,
                    background: active ? (dark ? "rgba(99,102,241,0.2)" : "rgba(99,102,241,0.1)") : "transparent",
                    color: active ? "#6366f1" : t.text,
                    transition: "all .15s",
                  }}>
                  <span>{f.label}</span>{" "}
                  <span style={{ fontWeight: 800 }}>{f.count}</span>{" "}
                  <span style={{ fontSize: 10, color: t.textMuted }}>{f.pct}%</span>
                  {f.change != null && f.change !== 0 && (
                    <span style={{ fontSize: 10, fontWeight: 700, marginLeft: 4,
                      color: f.key === "out" ? (f.change > 0 ? "#ef4444" : "#10b981") : (f.change > 0 ? "#10b981" : "#ef4444") }}>
                      {f.change > 0 ? "\u2191" : "\u2193"}{Math.abs(f.change)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* POSITION TREND CHART */}
          {snapshots.length >= 1 && chartData && (
            <div style={{ ...card, padding: 16, marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 12 }}>
                {CHART_TABS.map(ct => (
                  <button key={ct.key} onClick={() => setChartTab(ct.key)}
                    style={{
                      padding: "4px 10px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600,
                      background: chartTab === ct.key ? "#6366f1" : "transparent",
                      color: chartTab === ct.key ? "#fff" : t.textMuted,
                    }}>{ct.label}</button>
                ))}
              </div>
              <div style={{ height: 200 }}>
                <Line data={chartData} options={chartOpts} />
              </div>
            </div>
          )}

          {/* ТОП-5 КОНКУРЕНТІВ */}
          {topCompetitors && (
            <div style={{ ...card, padding: "12px 16px", marginBottom: 14, borderLeft: "4px solid #f59e0b" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>🏆 Топ-5 конкурентів у видачі</span>
                <span style={{ fontSize: 11, color: t.textMuted }}>за знімком {formatDateUkr(topCompetitors.date) || ""} · скільки разів домен зустрівся у ТОПі по ваших ключах</span>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {topCompetitors.list.map((c, i) => (
                  <a key={c.domain} href={`https://${c.domain}`} target="_blank" rel="noopener noreferrer"
                    style={{ textDecoration: "none", flex: "1 1 180px", minWidth: 160, display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(245,158,11,0.07)" : "rgba(245,158,11,0.06)", border: `1px solid ${t.border}` }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: ["#f59e0b", "#9ca3af", "#b45309", "#6b7280", "#6b7280"][i] }}>#{i + 1}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.domain}</div>
                      <div style={{ fontSize: 10.5, color: t.textMuted }}>{c.count} ключів · сер. поз. {c.avgPos}</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* КОНКУРЕНТИ КЛІЄНТА */}
          {clientCompetitors && clientCompetitors.list.length > 0 && (
            <div style={{ ...card, padding: "12px 16px", marginBottom: 14, borderLeft: "4px solid #6366f1" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>🎯 Конкуренти клієнта</span>
                <span style={{ fontSize: 11, color: t.textMuted }}>
                  {clientCompetitors.hasData
                    ? `за знімком ${formatDateUkr(clientCompetitors.date) || ""} · присутність у ТОПі по ваших ключах`
                    : "ще немає знімку з даними конкурентів — зʼявиться після наступної перевірки позицій"}
                </span>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {clientCompetitors.list.map((c) => (
                  <a key={c.domain} href={`https://${c.domain}`} target="_blank" rel="noopener noreferrer"
                    style={{ textDecoration: "none", flex: "1 1 180px", minWidth: 160, display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(99,102,241,0.07)" : "rgba(99,102,241,0.06)", border: `1px solid ${t.border}` }}>
                    <span style={{ fontSize: 16, fontWeight: 800, color: c.count ? "#6366f1" : t.textMuted }}>
                      {c.count ? `#${c.best ?? "—"}` : "—"}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.domain}</div>
                      <div style={{ fontSize: 10.5, color: t.textMuted }}>
                        {c.count ? `${c.count} ключів · сер. поз. ${c.avgPos} · найкраща #${c.best}` : "не знайдено в ТОПі"}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* KEYWORDS TABLE */}
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderBottom: bd }}>
              <span style={{ fontSize: 12, fontWeight: 700 }}>{filtered.length} ключів</span>
              {viewMode === "groups" && <span style={{ fontSize: 11, color: t.textMuted }}>/ {groups.length} сторінок</span>}
              <button onClick={selectAllFiltered} title="Вибрати / зняти всі відфільтровані ключі" style={{ ...btnSm, fontSize: 10, padding: "3px 8px" }}>☑ Вибрати всі</button>
              <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                {[
                  { key: "position", label: "Позиція ↑" },
                  { key: "keywords", label: "Кількість" },
                ].map(s => (
                  <button key={s.key} onClick={() => setGroupSort(s.key)}
                    style={{ ...btnSm, fontSize: 10, background: groupSort === s.key ? "#6366f1" : "transparent",
                      color: groupSort === s.key ? "#fff" : t.textMuted, border: "none", padding: "3px 8px" }}>
                    {s.label}
                  </button>
                ))}
                <span style={{ width: 1, height: 16, background: t.border, margin: "0 2px" }} />
                {[
                  { key: "posAsc", label: "Поз ↑" },
                  { key: "posDesc", label: "Поз ↓" },
                  { key: "freqDesc", label: "Частотн." },
                ].map(s => (
                  <button key={s.key} onClick={() => setKwSort(s.key)}
                    style={{ ...btnSm, fontSize: 10, background: kwSort === s.key ? "#8b5cf6" : "transparent",
                      color: kwSort === s.key ? "#fff" : t.textMuted, border: "none", padding: "3px 8px" }}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {selectedKw.size > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", borderBottom: bd, background: dark ? "rgba(99,102,241,0.10)" : "rgba(99,102,241,0.06)", position: "sticky", top: 0, zIndex: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 700 }}>Вибрано {selectedKw.size} ключів</span>
                <button onClick={sendToLinkGen} style={{ ...btnP, fontSize: 12, padding: "6px 12px" }}>📝 Відправити на генерацію рейтингових →</button>
                <button onClick={openSparkTraffic} style={{ ...btnG, fontSize: 12, padding: "6px 12px" }}>🚀 Підключити ПФ</button>
                <span style={{ fontSize: 10, color: t.textMuted }}>2 ключі/стаття, посилання на різні сторінки</span>
                <button onClick={clearSelect} style={{ ...btnG, fontSize: 11, marginLeft: "auto" }}>Зняти вибір</button>
              </div>
            )}

            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...cellLeft, fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", borderBottom: `2px solid ${t.border}` }}>Ключ</th>
                  <th style={{ ...cellStyle, fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", borderBottom: `2px solid ${t.border}`, width: 80 }}>Частотність</th>
                  {snapCols.map(sc => {
                    const isActive = sc.idx === selSnap;
                    const arrow = isActive ? (kwSort === "posAsc" ? " ▲" : kwSort === "posDesc" ? " ▼" : "") : "";
                    return (
                      <th key={sc.idx}
                        onClick={() => onSnapColClick(sc.idx)}
                        title="Клік — сортувати за позиціями (від 1 до найгіршої). Повторний клік — інверсія."
                        style={{ ...cellStyle, fontSize: 10, fontWeight: 700, color: isActive ? "#6366f1" : t.textMuted,
                          textTransform: "uppercase", borderBottom: `2px solid ${isActive ? "#6366f1" : t.border}`,
                          width: 90, cursor: "pointer", userSelect: "none" }}>
                        {sc.label}{arrow}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {viewMode === "groups" ? (
                  <>
                    {groups.map(g => {
                      const isOpen = expanded[g.url] === true;
                      const name = structureMap[g.url] || getPathname(g.url);
                      return (
                        <GroupRows key={g.url} group={g} name={name} isOpen={isOpen}
                          onToggle={() => toggleGroup(g.url)} snapCols={snapCols} posCell={posCell}
                          cellStyle={cellStyle} cellLeft={cellLeft} dark={dark} t={t} bd={bd}
                          tags={tags} showTagInput={showTagInput} setShowTagInput={setShowTagInput}
                          tagInput={tagInput} setTagInput={setTagInput} addTag={addTag} removeTag={removeTag}
                          onKeywordClick={setKwModal} selectedKw={selectedKw} onToggleSelect={toggleKwSelect} />
                      );
                    })}
                    {groups.length === 0 && (
                      <tr><td colSpan={2 + snapCols.length} style={{ ...cellStyle, padding: 30, color: t.textMuted }}>Немає даних</td></tr>
                    )}
                  </>
                ) : (
                  /* FLAT VIEW — all keywords without groups */
                  filtered.sort((a, b) => kwSort === "posAsc" ? (a.position||999)-(b.position||999) : kwSort === "posDesc" ? (b.position||0)-(a.position||0) : (b.frequency||0)-(a.frequency||0)).slice(0, 500).map((k, i) => (
                    <tr key={i} onClick={() => setKwModal(k)} style={{ background: i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)"), cursor: "pointer" }}>
                      <td style={cellLeft}>
                        <div style={{ fontSize: 12, fontWeight: 500 }}>
                          <input type="checkbox" checked={!!selectedKw.has(k.keyword.toLowerCase())}
                            onClick={e => e.stopPropagation()} onChange={() => toggleKwSelect(k)}
                            style={{ marginRight: 8, cursor: "pointer", verticalAlign: "middle" }} title="Вибрати ключ для генерації рейтингових" />
                          {k.keyword}
                          <UrlMatchBadge targetUrl={k.url} serpUrl={k.serpUrl} />
                        </div>
                        <div style={{ fontSize: 10, color: t.textMuted }}>{getPathname(k.url)}</div>
                        {/* Tags */}
                        <div style={{ display: "flex", gap: 3, marginTop: 2, flexWrap: "wrap" }}>
                          {(tags[k.keyword.toLowerCase()] || []).map(tag => (
                            <span key={tag} style={{ fontSize: 9, padding: "1px 5px", borderRadius: 4, background: "#6366f120", color: "#6366f1", cursor: "pointer" }}
                              onClick={e => { e.stopPropagation(); removeTag(k.keyword, tag); }}>{tag} ✕</span>
                          ))}
                          {showTagInput === k.keyword ? (
                            <input value={tagInput} onChange={e => setTagInput(e.target.value)} autoFocus onClick={e => e.stopPropagation()}
                              onKeyDown={e => { if (e.key === "Enter") addTag(k.keyword, tagInput); if (e.key === "Escape") setShowTagInput(null); }}
                              onBlur={() => { if (tagInput.trim()) addTag(k.keyword, tagInput); else setShowTagInput(null); }}
                              style={{ fontSize: 9, padding: "1px 4px", border: `1px solid ${t.border}`, borderRadius: 4, width: 60, background: "transparent", color: t.text }} />
                          ) : (
                            <span onClick={e => { e.stopPropagation(); setShowTagInput(k.keyword); setTagInput(""); }}
                              style={{ fontSize: 9, padding: "1px 4px", borderRadius: 4, border: `1px dashed ${t.border}`, color: t.textMuted, cursor: "pointer" }}>+тег</span>
                          )}
                        </div>
                      </td>
                      <td style={cellStyle}>{k.frequency || 0}</td>
                      {snapCols.map(sc => posCell(k.keyword, sc.idx))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {kwModal && (
        <KeywordModal kw={kwModal} articleCount={artByKeyword[String(kwModal.keyword || "").toLowerCase()] || 0} onClose={() => setKwModal(null)} t={t} dark={dark} btnG={btnG} />
      )}

      {stModal && (
        <SparkTrafficModal data={stModal} onClose={() => setStModal(null)} apiFetch={apiFetch} activeId={activeId}
          t={t} dark={dark} btnP={btnP} btnG={btnG} inp={inp} flash={flash} onDone={clearSelect} onTagged={(kws) => applyTagBulk(kws, "ПФ")} />
      )}
    </div>
  );
}

/* ---- SparkTraffic «Підключити ПФ» modal ---- */
// size у цьому акаунті = пул кредитів (eco/exp/pro), а НЕ тарифний enum nano/mini.
// Обсяг трафіку = visitsPerDay (поле speed у API = РЕАЛЬНА к-сть візитів/день, не %).
// Гео: лише «весь світ» або «місто» за числовим ID SparkTraffic (країна через API не зберігається).
const ST_TIME_OPTS = ["5sec", "30sec", "1min", "2min", "3min", "4min", "5min"];
const ST_DEFAULTS = {
  pool: "eco", visitsBase: 1000, randomRate: 15, time_on_page: "1min", // база переходів/день + Random Rate % (коливання ±)
  bounce_rate: 40, return_rate: 20, desktop_rate: 55,
  scroll_events: 0, click_outbound_events: 0, form_submit_events: 0,
  auto_renew: false, geoMode: "city", cityGeo: "1012852", // деф: Київ (ID SparkTraffic)
  languages: "uk,ru", randomizeTime: false, // HTTP Accept-Language + рандом часу на сторінці (клієнтський)
};
// База + Random Rate % → діапазон [min,max] (як у вебі: 200 + рандом → 176–206).
function stBand(base, rate) {
  const b = Math.max(1, Math.round(Number(base) || 0));
  const r = Math.max(0, Math.min(90, Number(rate) || 0)) / 100;
  return { lo: Math.max(1, Math.round(b * (1 - r))), hi: Math.round(b * (1 + r)) };
}

function SparkTrafficModal({ data, onClose, apiFetch, activeId, t, dark, btnP, btnG, inp, flash, onDone, onTagged }) {
  const keys = data.keys || [];
  const [cfg, setCfg] = useState(ST_DEFAULTS);
  const [balance, setBalance] = useState(null);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    apiFetch(`/api/projects/${activeId}/positions/sparktraffic/balance`)
      .then(r => r.json()).then(d => setBalance(d?.balance || null)).catch(() => setBalance(null));
  }, [activeId, apiFetch]);

  const set = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));

  const send = async () => {
    setSending(true); setResults(null);
    const items = keys.map(k => ({ keyword: k.keyword, url: k.url }));
    const { lo, hi } = stBand(cfg.visitsBase, cfg.randomRate); // база+RandomRate → діапазон для бекенду
    const settings = { ...cfg, visitsMin: lo, visitsMax: hi };
    try {
      const r = await apiFetch(`/api/projects/${activeId}/positions/sparktraffic/send`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ items, settings }),
      });
      const d = await r.json();
      if (!r.ok) { flash && flash(d.error || "Помилка SparkTraffic"); setSending(false); return; }
      setResults(d.results || []);
      const okKws = (d.results || []).filter(r => r.ok).map(r => r.keyword);
      if (okKws.length && onTagged) onTagged(okKws);
      flash && flash(`SparkTraffic: створено ${d.created}/${d.total} проєктів`);
    } catch (e) { flash && flash("Помилка мережі: " + e.message); }
    setSending(false);
  };

  const ov = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 };
  const box = { background: dark ? "#1a1a2e" : "#fff", color: t.text, borderRadius: 12, padding: 20, width: 660, maxHeight: "88vh", overflow: "auto", border: `1px solid ${t.border}` };
  const cell = { padding: "5px 8px", fontSize: 12, borderBottom: `1px solid ${t.border}` };
  const lbl = { fontSize: 11, color: t.textMuted, display: "block", marginBottom: 3 };
  const fieldNum = { ...inp, width: "100%", fontSize: 12, padding: "4px 8px", boxSizing: "border-box" };
  const resByKw = {}; (results || []).forEach(r => { resByKw[String(r.keyword).toLowerCase()] = r; });

  const desc = { fontSize: 10, color: t.textMuted, marginTop: 2, lineHeight: 1.3 };
  // повзунок 0..100 з підписом і поясненням
  const Slider = ({ k, label, info }) => (
    <div>
      <label style={lbl}>{label}: <strong style={{ color: t.text }}>{cfg[k]}%</strong></label>
      <input type="range" min={0} max={100} value={cfg[k]} onChange={e => set(k, Number(e.target.value))}
        style={{ width: "100%" }} />
      <div style={desc}>{info}</div>
    </div>
  );

  return (
    <div style={ov} onClick={onClose}>
      <div style={box} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>🚀 Підключити ПФ</h3>
          <button onClick={onClose} style={{ ...btnG, marginLeft: "auto", fontSize: 12 }}>✕</button>
        </div>

        <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 12 }}>
          Organic-трафік: на кожен ключ — окремий проєкт (візити по пошуковому запиту на його сторінку). Налаштування застосуються до всіх вибраних ключів.
          {balance && <span> · Баланс: <strong>eco {Number(balance.eco || 0).toLocaleString()}</strong>, exp {Number(balance.exp || 0).toLocaleString()}, pro {Number(balance.pro || 0).toLocaleString()}</span>}
        </div>

        {/* ── Панель налаштувань ── */}
        <div style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: 12, marginBottom: 14, background: dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              {(() => { const { lo, hi } = stBand(cfg.visitsBase, cfg.randomRate); return (
                <>
                  <label style={lbl}>Переходів на день: <strong style={{ color: t.text }}>{Number(cfg.visitsBase || 0).toLocaleString()}</strong></label>
                  <input type="number" min={200} step={50} value={cfg.visitsBase}
                    onChange={e => set("visitsBase", Math.max(200, Number(e.target.value) || 200))} style={fieldNum} />
                  <input type="range" min={200} max={10000} step={50} value={Math.min(10000, Math.max(200, Number(cfg.visitsBase) || 200))}
                    onChange={e => set("visitsBase", Number(e.target.value))} style={{ width: "100%", marginTop: 4 }} />
                  <label style={{ ...lbl, marginTop: 6 }}>Random Rate: <strong style={{ color: t.text }}>±{cfg.randomRate}%</strong> <span style={{ color: t.textMuted }}>→ {lo.toLocaleString()}–{hi.toLocaleString()}</span></label>
                  <input type="range" min={0} max={50} step={1} value={cfg.randomRate}
                    onChange={e => set("randomRate", Number(e.target.value))} style={{ width: "100%" }} />
                  <div style={desc}>Кожен ключ дістане випадкове число в межах ±Random Rate від бази (0% → рівно база).</div>
                </>
              ); })()}
            </div>
            <div>
              <label style={lbl}>Пул кредитів</label>
              <select value={cfg.pool} onChange={e => set("pool", e.target.value)} style={fieldNum}>
                <option value="eco">eco (рясний)</option>
                <option value="exp">exp (expert)</option>
                <option value="pro">pro</option>
              </select>
              <div style={desc}>З якого балансу списувати. eco — найдешевший і найбільший запас.</div>
            </div>
            <div>
              <label style={lbl}>Час на сторінці</label>
              <select value={cfg.time_on_page} onChange={e => set("time_on_page", e.target.value)} style={fieldNum}>
                {ST_TIME_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              <div style={desc}>Скільки бот «читає» сторінку за візит. Більше = глибша поведінка.</div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <Slider k="bounce_rate" label="Показник відмов" info="% візитів, що йдуть одразу (одна сторінка, без дій). Менше = «залученіше»." />
            <Slider k="return_rate" label="Повернення" info="% сесій від тих самих «юзерів» повторно. Імітує лояльну аудиторію." />
            <Slider k="desktop_rate" label="Desktop" info="% візитів з десктопу, решта — мобільні. Постав під свою реальну аудиторію." />
            <Slider k="scroll_events" label="Скрол-події" info="% візитів, де бот гортає сторінку вниз." />
            <Slider k="click_outbound_events" label="Кліки по посиланнях" info="% візитів з кліком по внутрішньому/зовнішньому лінку." />
            <Slider k="form_submit_events" label="Сабміт форм" info="% візитів із відправкою форми (заявка/підписка)." />
          </div>

          {/* Гео */}
          <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 10 }}>
            <label style={lbl}>Звідки трафік (гео)</label>
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                <input type="radio" name="st-geo" checked={cfg.geoMode === "global"} onChange={() => set("geoMode", "global")} />
                Весь світ
              </label>
              <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                <input type="radio" name="st-geo" checked={cfg.geoMode === "city"} onChange={() => set("geoMode", "city")} />
                Місто
              </label>
              {cfg.geoMode === "city" && (
                <>
                  <input type="text" value={cfg.cityGeo} onChange={e => set("cityGeo", e.target.value)}
                    placeholder="ID міста SparkTraffic, напр. 1012852"
                    style={{ ...inp, fontSize: 12, padding: "4px 8px", width: 200 }} />
                  {String(cfg.cityGeo).trim() === "1012852" && <span style={{ fontSize: 11, color: "#10b981" }}>Київ</span>}
                </>
              )}
            </div>
            <div style={desc}>
              {cfg.geoMode === "city"
                ? "SparkTraffic ідентифікує місто числовим ID (Київ = 1012852). Кілька — через кому. Гео по країні через API недоступне."
                : "Трафік з усього світу. Для гео-таргета обери «Місто» і встав ID SparkTraffic."}
            </div>
          </div>

          {/* Мова браузера + рандом часу */}
          <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 10, marginTop: 10, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ minWidth: 180 }}>
              <label style={lbl}>Мова браузера (Accept-Language)</label>
              <input type="text" value={cfg.languages} onChange={e => set("languages", e.target.value)}
                placeholder="uk,ru" style={{ ...inp, fontSize: 12, padding: "4px 8px", width: 160 }} />
              <div style={desc}>Коди мов через кому (uk,ru,en). Як виглядає мова відвідувача.</div>
            </div>
            <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }} title="Кожному ключу — випадковий час на сторінці з діапазону (5sec…5min). Аналог «Randomize time» з вебу.">
              <input type="checkbox" checked={cfg.randomizeTime} onChange={e => set("randomizeTime", e.target.checked)} />
              Рандом часу на сторінці
            </label>
          </div>

          <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, cursor: "pointer", marginTop: 10 }}>
            <input type="checkbox" checked={cfg.auto_renew} onChange={e => set("auto_renew", e.target.checked)} />
            Авто-продовження кожні 30 днів
            <span style={{ ...desc, marginTop: 0 }}>(інакше проєкт сам зупиниться через місяць)</span>
          </label>
        </div>

        {/* ── Список ключів ── */}
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 14 }}>
          <thead>
            <tr style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase" }}>
              <th style={{ ...cell, textAlign: "left" }}>Ключ → сторінка ({keys.length})</th>
              {results && <th style={{ ...cell, width: 110 }}>Статус</th>}
            </tr>
          </thead>
          <tbody>
            {keys.map((k, i) => {
              const kl = k.keyword.toLowerCase();
              const r = resByKw[kl];
              return (
                <tr key={i}>
                  <td style={{ ...cell, textAlign: "left" }}>
                    <div style={{ fontWeight: 500 }}>{k.keyword}</div>
                    <div style={{ fontSize: 10, color: t.textMuted, maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{k.url}</div>
                  </td>
                  {results && <td style={{ ...cell, textAlign: "center", fontSize: 11 }}>
                    {r ? (r.ok ? <span style={{ color: "#10b981" }} title={r.unique_id || ""}>✓ {r.speed != null ? `${Number(r.speed).toLocaleString()}/день` : (r.unique_id || "створено")}</span>
                              : <span style={{ color: "#ef4444" }} title={r.error || ""}>✕ {r.error || "помилка"}</span>) : "—"}
                  </td>}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* ── Разом (Total НА ДЕНЬ, як у вебі SparkTraffic) — діапазон ── */}
        {!results && (() => {
          const { lo, hi } = stBand(cfg.visitsBase, cfg.randomRate);
          const dayLo = lo * keys.length, dayHi = hi * keys.length; // сумарно/день по всіх ключах
          const low = lo < 200; // мінімум SparkTraffic — 200
          const fixed = lo === hi;
          return (
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", padding: "10px 12px", marginBottom: 12, borderRadius: 8,
              background: low ? "rgba(239,68,68,.10)" : "rgba(16,185,129,.10)", border: `1px solid ${low ? "#ef4444" : "#10b981"}` }}>
              <span style={{ fontSize: 12, fontWeight: 700 }}>Разом на день:</span>
              <span style={{ fontSize: 18, fontWeight: 800, color: low ? "#ef4444" : "#10b981" }}>
                {fixed ? dayLo.toLocaleString() : `${dayLo.toLocaleString()}–${dayHi.toLocaleString()}`}
              </span>
              <span style={{ fontSize: 11, color: t.textMuted }}>
                візитів/день = {fixed ? lo.toLocaleString() : `${lo.toLocaleString()}–${hi.toLocaleString()}`}/ключ × {keys.length} ключ. (ціль; фактично SparkTraffic дає менше й розганяється поступово)
              </span>
              {low && <span style={{ fontSize: 11, color: "#ef4444", fontWeight: 600, marginLeft: "auto" }}>⚠ мінімум SparkTraffic — 200/день (менше не приймається)</span>}
            </div>
          );
        })()}

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {!results ? (
            <button onClick={send} disabled={sending} style={{ ...btnP, fontSize: 13, opacity: sending ? 0.6 : 1 }}>
              {sending ? "Створюю проєкти…" : `🚀 Запустити трафік на ${keys.length} ключів`}
            </button>
          ) : (
            <button onClick={() => { onDone && onDone(); onClose(); }} style={{ ...btnP, fontSize: 13 }}>Готово</button>
          )}
          <button onClick={onClose} style={{ ...btnG, fontSize: 12 }}>Закрити</button>
          <span style={{ fontSize: 10, color: t.textMuted, marginLeft: "auto" }}>Списується обраний пул. Проєкти живуть 30 днів.</span>
        </div>
      </div>
    </div>
  );
}

/* ---- Group rows sub-component ---- */
function GroupRows({ group, name, isOpen, onToggle, snapCols, posCell, cellStyle, cellLeft, dark, t, bd, tags, showTagInput, setShowTagInput, tagInput, setTagInput, addTag, removeTag, onKeywordClick, selectedKw, onToggleSelect }) {
  const g = group;
  const headerBg = dark ? "rgba(99,102,241,0.06)" : "rgba(99,102,241,0.03)";
  return (
    <>
      {/* Group header row */}
      <tr onClick={onToggle} style={{ cursor: "pointer", background: headerBg }}>
        <td style={{ ...cellLeft, fontWeight: 700, fontSize: 12, padding: "8px 10px", borderBottom: bd }} colSpan={2 + snapCols.length}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 10, transition: "transform .15s", display: "inline-block",
              transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}>&#9654;</span>
            <span style={{ maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={g.url}>
              {name}
            </span>
            <span title={`Знайдено в Google ${g.foundCount} з ${g.keywords.length} ключів (решта поза ТОП — рахується як 100 у середній позиції)`}
              style={{ fontSize: 10, color: "#6366f1", fontWeight: 700, background: "rgba(99,102,241,0.1)",
              padding: "1px 6px", borderRadius: 4 }}>🔑 {g.keywords.length} ключів · знайдено {g.foundCount}</span>
            {g.articleCount > 0 && <span title="Скільки статей із посиланнями написано на цю групу (модуль «Генерація статей для ссилок»)"
              style={{ fontSize: 10, color: "#10b981", fontWeight: 700, background: "rgba(16,185,129,0.12)", padding: "1px 6px", borderRadius: 4 }}>📝 {g.articleCount} статей</span>}
            <span title="Сума частотності всіх ключів цієї сторінки" style={{ fontSize: 10, color: t.textMuted }}>
              загальна частотність: {(g.totalFreq || 0).toLocaleString("uk-UA")}
            </span>
            {g.avgPos < 999 && (
              <span
                title={g.avgPos <= 10
                  ? `Середня позиція ${g.avgPos} по всіх ${g.keywords.length} ключах (ключі поза ТОП = 100). Сторінка в ТОП — добре. Увага: масово мету міняти не можна (мета працює), лише поштучно.`
                  : `Середня позиція ${g.avgPos} по всіх ${g.keywords.length} ключах (ключі поза ТОП = 100). Поза ТОП — слабко. Мету можна міняти вільно.`}
                style={{
                  fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                  color: g.avgPos <= 10 ? "#16a34a" : "#dc2626",
                  background: g.avgPos <= 10 ? "rgba(22,163,74,0.12)" : "rgba(220,38,38,0.12)",
                }}>
                {g.avgPos <= 10 ? "🟢" : "🔴"} середня позиція: {g.avgPos}
              </span>
            )}
          </div>
        </td>
      </tr>
      {/* Keyword rows */}
      {isOpen && g.keywords.map((k, ki) => (
        <tr key={ki} onClick={() => onKeywordClick && onKeywordClick(k)} style={{ background: ki % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,.015)" : "rgba(0,0,0,.01)"), cursor: "pointer" }}>
          <td style={{ ...cellLeft, fontSize: 12, paddingLeft: 28 }}>
            <input type="checkbox" checked={!!(selectedKw && selectedKw.has(k.keyword.toLowerCase()))}
              onClick={e => e.stopPropagation()} onChange={() => onToggleSelect && onToggleSelect(k)}
              style={{ marginRight: 8, cursor: "pointer", verticalAlign: "middle" }} title="Вибрати ключ для генерації рейтингових" />
            {k.keyword}
            <UrlMatchBadge targetUrl={k.url} serpUrl={k.serpUrl} />
            {tags && (tags[k.keyword.toLowerCase()] || []).map(tag => (
              <span key={tag} onClick={e => { e.stopPropagation(); removeTag(k.keyword, tag); }} style={{ fontSize: 8, padding: "0 4px", marginLeft: 3, borderRadius: 3, background: "#6366f120", color: "#6366f1", cursor: "pointer" }}>{tag}✕</span>
            ))}
            {tags && showTagInput === k.keyword ? (
              <input value={tagInput} onChange={e => setTagInput(e.target.value)} autoFocus onClick={e => e.stopPropagation()}
                onKeyDown={e => { if (e.key === "Enter") addTag(k.keyword, tagInput); if (e.key === "Escape") setShowTagInput(null); }}
                onBlur={() => { if (tagInput.trim()) addTag(k.keyword, tagInput); else setShowTagInput(null); }}
                style={{ fontSize: 9, padding: "0 3px", marginLeft: 3, border: `1px solid ${t.border}`, borderRadius: 3, width: 50, background: "transparent", color: t.text }} />
            ) : tags && (
              <span onClick={e => { e.stopPropagation(); setShowTagInput(k.keyword); setTagInput(""); }} style={{ fontSize: 8, marginLeft: 3, color: t.textMuted, cursor: "pointer", opacity: 0.5 }}>+</span>
            )}
          </td>
          <td style={{ ...cellStyle, color: t.textMuted }}>{k.frequency || "--"}</td>
          {snapCols.map(sc => posCell(k.keyword, sc.idx))}
        </tr>
      ))}
    </>
  );
}

const COUNTRIES = [
  { gl: "ua", name: "Ukraine", cities: ["Kyiv", "Kharkiv", "Odesa", "Lviv", "Dnipro", "Zaporizhzhia", "Vinnytsia", "Chernihiv", "Poltava", "Cherkasy", "Lutsk", "Ternopil", "Khmelnytskyi", "Uzhhorod", "Ivano-Frankivsk", "Rivne", "Sumy", "Mykolaiv", "Kropyvnytskyi"] },
  { gl: "pl", name: "Poland", cities: ["Warsaw", "Krakow", "Lodz", "Wroclaw", "Poznan", "Gdansk"] },
  { gl: "us", name: "United States", cities: ["New York", "Los Angeles", "Chicago", "Houston", "Miami", "San Francisco", "Seattle", "Boston", "Atlanta"] },
  { gl: "gb", name: "United Kingdom", cities: ["London", "Manchester", "Birmingham", "Glasgow", "Edinburgh"] },
  { gl: "de", name: "Germany", cities: ["Berlin", "Hamburg", "Munich", "Cologne", "Frankfurt"] },
  { gl: "cz", name: "Czech Republic", cities: ["Prague", "Brno", "Ostrava"] },
  { gl: "ro", name: "Romania", cities: ["Bucharest", "Cluj-Napoca", "Timisoara"] },
  { gl: "es", name: "Spain", cities: ["Madrid", "Barcelona", "Valencia"] },
  { gl: "fr", name: "France", cities: ["Paris", "Lyon", "Marseille"] },
  { gl: "it", name: "Italy", cities: ["Rome", "Milan", "Naples"] },
  { gl: "kz", name: "Kazakhstan", cities: ["Almaty", "Astana", "Shymkent"] },
  { gl: "tr", name: "Turkey", cities: ["Istanbul", "Ankara", "Izmir"] },
  { gl: "ca", name: "Canada", cities: ["Toronto", "Montreal", "Vancouver"] },
];

function GeoModal({ initial, onClose, onSubmit, onSaveOnly, hasHistory, t, dark, btnP, btnG, inp }) {
  const [gl, setGl] = useState(initial?.gl || "ua");
  const [city, setCity] = useState(initial?.city || "");
  const country = COUNTRIES.find(c => c.gl === gl) || COUNTRIES[0];
  const build = () => {
    const cleanCity = (city || "").trim();
    const location = cleanCity ? `${cleanCity},${country.name}` : country.name;
    return { gl: country.gl, country: country.name, city: cleanCity, location };
  };
  const submit = () => onSubmit(build());
  const saveOnly = () => onSaveOnly && onSaveOnly(build());
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: dark ? "#171727" : "#fff", padding: 24, borderRadius: 10, maxWidth: 460, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,.4)" }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700, color: t.text }}>🌍 Налаштування геолокації</h3>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: t.textMuted, lineHeight: 1.5 }}>
          Виберіть країну і місто, в яких Google буде давати результати для перевірки позицій. Можна змінити пізніше.
        </p>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Країна</div>
          <select value={gl} onChange={e => { setGl(e.target.value); setCity(""); }}
            style={{ ...inp, width: "100%", fontSize: 13 }}>
            {COUNTRIES.map(c => <option key={c.gl} value={c.gl}>{c.name}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Місто (опціонально)</div>
          <input type="text" value={city} onChange={e => setCity(e.target.value)}
            placeholder={`Напр.: ${country.cities[0]}`} list="city-suggestions"
            style={{ ...inp, width: "100%", fontSize: 13 }} />
          <datalist id="city-suggestions">
            {country.cities.map(c => <option key={c} value={c} />)}
          </datalist>
          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>
            Пусто = вся країна. Місто дає геолокальні результати (для локального бізнесу).
          </div>
        </div>

        <div style={{ marginBottom: 16, padding: 10, borderRadius: 6, background: dark ? "rgba(99,102,241,.08)" : "rgba(99,102,241,.05)", fontSize: 12, color: t.textMuted }}>
          Google буде шукати в: <strong style={{ color: t.text }}>{city ? `${city}, ` : ""}{country.name}</strong> (gl={country.gl})
        </div>

        {hasHistory && (
          <div style={{ marginBottom: 14, padding: 10, borderRadius: 6, background: dark ? "rgba(61,245,200,.08)" : "rgba(16,185,129,.06)", fontSize: 12, color: t.textMuted, lineHeight: 1.5 }}>
            ✓ Історія позицій збережеться — зміна регіону впливає лише на наступні зняття.
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button onClick={onClose} style={{ ...btnG, fontSize: 12 }}>Скасувати</button>
          {onSaveOnly && <button onClick={saveOnly} style={{ ...btnG, fontSize: 12 }}>Зберегти регіон</button>}
          <button onClick={submit} style={{ ...btnP, fontSize: 12 }}>Зберегти і перевірити зараз</button>
        </div>
      </div>
    </div>
  );
}

function formatDateUkr(d) {
  if (!d) return "";
  const s = String(d).slice(0, 10); // strip time part if ISO
  const parts = s.split("-");
  if (parts.length !== 3) return s;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

function normalizeUrlPath(u) {
  if (!u) return "";
  try {
    const parsed = new URL(u);
    return (parsed.hostname.replace(/^www\./, "") + parsed.pathname.replace(/\/+$/, "")).toLowerCase();
  } catch {
    return String(u).toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/+$/, "");
  }
}

function UrlMatchBadge({ targetUrl, serpUrl }) {
  const chip = { fontSize: 9, fontWeight: 700, padding: "1px 6px", marginLeft: 6, borderRadius: 4, textDecoration: "none", cursor: "help", whiteSpace: "nowrap", display: "inline-block", verticalAlign: "middle" };
  if (!serpUrl) {
    return <span style={{ ...chip, cursor: "default", background: "#88888820", color: "#888" }} title="Немає даних: позиція не знайдена в TOP-100">немає даних</span>;
  }
  const match = normalizeUrlPath(targetUrl) === normalizeUrlPath(serpUrl);
  const c = match ? "#10b981" : "#ef4444";
  const tip = match
    ? `Збігається з ціллю Spalah\n\nГугл: ${serpUrl}\nSpalah: ${targetUrl}`
    : `НЕ збігається — Google ранжирує іншу сторінку!\n\nГугл: ${serpUrl}\nSpalah: ${targetUrl || "(не задано)"}`;
  return (
    <a href={serpUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
      title={tip}
      style={{ ...chip, background: `${c}20`, color: c }}
    >
      {match ? "співпадає" : "не співпадає"}
    </a>
  );
}

// ─── Keyword detail popup ─────────────────────────────────────────────
function KeywordModal({ kw, articleCount = 0, onClose, t, dark, btnG }) {
  const target = kw.url || "";
  const serp = kw.serpUrl || "";
  const competitors = Array.isArray(kw.competitors) ? kw.competitors : [];

  let status, statusColor, statusBg, statusIcon, statusDesc;
  const _mpos = (kw.manual && kw.position != null) ? kw.position : null;
  if (_mpos != null && !serp) {
    const _b = _mpos <= 3 ? "ТОП-3" : _mpos <= 10 ? "ТОП-10" : _mpos <= 30 ? "ТОП-30" : "ТОП-" + _mpos;
    status = "Знайдено у " + _b + " (вручну)";
    statusIcon = _mpos <= 3 ? "🏆" : "✅";
    statusColor = _mpos <= 3 ? "#10b981" : _mpos <= 10 ? "#6366f1" : "#f59e0b";
    statusBg = "rgba(16,185,129,0.12)";
    statusDesc = "Позицію " + _mpos + " задано вручну — сайт у видачі за цим запитом.";
  } else if (!serp) {
    status = "Не у видачі";
    statusIcon = "❌";
    statusColor = "#ef4444";
    statusBg = "rgba(239,68,68,0.12)";
    statusDesc = "Сайт не знайдено у TOP-20 видачі для цього запиту.";
  } else if (normalizeUrlPath(target) === normalizeUrlPath(serp)) {
    status = "Цільова сторінка";
    statusIcon = "✅";
    statusColor = "#10b981";
    statusBg = "rgba(16,185,129,0.12)";
    statusDesc = "Google ранжує саме ту сторінку, що ви планували.";
  } else {
    status = "Інша сторінка";
    statusIcon = "⚠️";
    statusColor = "#f59e0b";
    statusBg = "rgba(245,158,11,0.12)";
    statusDesc = "Google ранжує не цільову сторінку. Перевірте конкуренцію цільової та поточної: може треба підсилити правильний URL або змінити ціль у СЯ.";
  }

  const overlayStyle = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9998,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
  };
  const modalStyle = {
    background: dark ? "#14141e" : "#fff", color: t.text,
    borderRadius: 12, maxWidth: 720, width: "100%", maxHeight: "90vh", overflow: "auto",
    border: `1px solid ${t.border}`, boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
  };
  const sectionStyle = {
    padding: "14px 22px", borderBottom: `1px solid ${t.border}`,
  };
  const labelStyle = { fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 4 };
  const urlBox = {
    fontSize: 12, padding: "8px 10px", borderRadius: 8, background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
    border: `1px solid ${t.border}`, wordBreak: "break-all", lineHeight: 1.4,
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ ...sectionStyle, display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, wordBreak: "break-word" }}>{kw.keyword}</div>
            <div style={{ display: "flex", gap: 16, fontSize: 11, color: t.textMuted, flexWrap: "wrap" }}>
              <span>Частота: <strong style={{ color: t.text }}>{kw.frequency || "—"}</strong></span>
              <span>Позиція: <strong style={{ color: kw.position == null ? t.textMuted : kw.position <= 3 ? "#10b981" : kw.position <= 10 ? "#6366f1" : kw.position <= 30 ? "#f59e0b" : "#ef4444" }}>{kw.position == null ? "—" : kw.position}</strong></span>
              <span title="Скільки статей із посиланнями написано на цей ключ (за анкором, модуль «Генерація статей для ссилок»)">📝 Статей: <strong style={{ color: articleCount > 0 ? "#10b981" : t.textMuted }}>{articleCount}</strong></span>
            </div>
          </div>
          <button onClick={onClose} style={{ ...btnG, fontSize: 16, lineHeight: 1, padding: "4px 10px" }} title="Закрити">✕</button>
        </div>

        {/* Status */}
        <div style={sectionStyle}>
          <div style={{
            display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 8,
            background: statusBg, border: `1px solid ${statusColor}40`,
          }}>
            <span style={{ fontSize: 20 }}>{statusIcon}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: statusColor }}>{status}</div>
              <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{statusDesc}</div>
            </div>
          </div>
        </div>

        {/* URLs */}
        <div style={sectionStyle}>
          <div style={{ marginBottom: 10 }}>
            <div style={labelStyle}>Цільова сторінка (Spalah СЯ)</div>
            {target ? (
              <a href={target} target="_blank" rel="noreferrer" style={{ ...urlBox, display: "block", color: "#6366f1", textDecoration: "none" }}>{target}</a>
            ) : (
              <div style={{ ...urlBox, color: t.textMuted, fontStyle: "italic" }}>не задано</div>
            )}
          </div>
          <div>
            <div style={labelStyle}>Релевантна сторінка (Google видача)</div>
            {serp ? (
              <a href={serp} target="_blank" rel="noreferrer" style={{ ...urlBox, display: "block", color: statusColor, textDecoration: "none" }}>{serp}</a>
            ) : (_mpos != null ? (
              <div style={{ ...urlBox, color: t.textMuted }}>{target || "—"} <span style={{ fontStyle: "italic" }}>(позиція задана вручну)</span></div>
            ) : (
              <div style={{ ...urlBox, color: t.textMuted, fontStyle: "italic" }}>не знайдено у TOP-20</div>
            ))}
          </div>
        </div>

        {/* Competitors */}
        <div style={{ padding: "14px 22px" }}>
          <div style={labelStyle}>Топ-3 конкуренти у видачі</div>
          {competitors.length === 0 ? (
            <div style={{ fontSize: 11, color: t.textMuted, fontStyle: "italic", padding: "8px 0" }}>
              Дані конкурентів зберігаються з наступного запуску перевірки позицій. Запустіть «Перевірити» вгорі щоб отримати їх.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {competitors.map((c, i) => (
                <a key={i} href={c.url} target="_blank" rel="noreferrer" style={{
                  ...urlBox, textDecoration: "none", color: t.text,
                  display: "flex", alignItems: "center", gap: 10,
                }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#6366f1", minWidth: 22 }}>#{c.position}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#6366f1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.domain || (() => { try { return new URL(c.url).hostname; } catch { return c.url; } })()}</div>
                    {c.title && <div style={{ fontSize: 11, color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.title}</div>}
                  </div>
                  <span style={{ fontSize: 10, color: t.textMuted }}>↗</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
